/**
 * İzin modülünün ortak parçaları — İzinler sayfası ve personel kartındaki
 * İzin sekmesi AYNI bileşenleri kullanır, iki ekran ayrışmasın.
 *
 * Yetki: talep açmak `employees/edit`; onay, ret, onaylı izni iptal, açılış
 * bakiyesi ve düzeltme yalnızca `timesheet/approve` (genel puantaj / tahakkuk
 * yetkilisi). Sunucu da aynı kuralı uygular; buradaki gizleme yalnızca kolaylık.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import SearchableSelect from '@/components/SearchableSelect';
import { Check, X, Ban, AlertTriangle, ChevronDown, ChevronRight, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

export const TALEP_DURUM = {
  beklemede: { etiket: 'Bekliyor', sinif: 'bg-[#7a6a2e]/10 text-[#7a6a2e]' },
  isleniyor: { etiket: 'İşleniyor', sinif: 'bg-[#2e6fa0]/10 text-[#2e6fa0]' },
  onaylandi: { etiket: 'Onaylandı', sinif: 'bg-[#3d6e4e]/10 text-[#3d6e4e]' },
  reddedildi: { etiket: 'Reddedildi', sinif: 'bg-[#7a3535]/10 text-[#7a3535]' },
  iptal: { etiket: 'İptal', sinif: 'bg-[#8a7e70]/10 text-[#8a7e70]' },
};

export const bugunIso = () => new Date().toLocaleDateString('en-CA');

const gunSayisi = (bas, bit) => {
  if (!bas || !bit || bit < bas) return 0;
  return Math.round((new Date(`${bit}T00:00:00`) - new Date(`${bas}T00:00:00`)) / 86400000) + 1;
};

const tarihBicim = (iso) => {
  if (!iso) return '—';
  const [y, a, g] = iso.slice(0, 10).split('-');
  return `${g}.${a}.${y}`;
};

const gunBicim = (n) => (Math.round((Number(n) || 0) * 100) / 100).toLocaleString('tr-TR');

const hata = (e, varsayilan) => toast.error(e?.response?.data?.detail || varsayilan, { duration: 7000 });

// ─── YENİ TALEP ───

export function YeniIzinDialog({ open, onClose, onKaydedildi, sabitPersonel = null }) {
  const bos = { employee_id: sabitPersonel?.id || '', izin_turu_id: '', baslangic: bugunIso(), bitis: bugunIso(), aciklama: '' };
  const [form, setForm] = useState(bos);
  const [personeller, setPersoneller] = useState([]);
  const [turler, setTurler] = useState([]);
  const [kaydediyor, setKaydediyor] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...bos, employee_id: sabitPersonel?.id || '' });
    api.get('/izin/turler').then(r => setTurler(r.data || [])).catch(() => {});
    if (!sabitPersonel) {
      api.get('/hr/employees').then(r => setPersoneller(r.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sabitPersonel?.id]);

  const tur = turler.find(t => t.id === form.izin_turu_id);
  const gun = gunSayisi(form.baslangic, form.bitis);

  const kaydet = async () => {
    if (!form.employee_id || !form.izin_turu_id) { toast.error('Personel ve izin türü seçilmeli'); return; }
    if (!gun) { toast.error('Bitiş tarihi başlangıçtan önce olamaz'); return; }
    setKaydediyor(true);
    try {
      const r = await api.post('/izin/talepler', form);
      toast.success('İzin talebi oluşturuldu');
      // Uyarılar engellemiyor ama onay verecek kişinin görmesi gerekiyor
      (r.data?.uyarilar || []).forEach(u => toast.warning(u, { duration: 9000 }));
      onKaydedildi?.(r.data);
      onClose();
    } catch (e) {
      hata(e, 'Talep oluşturulamadı');
    } finally { setKaydediyor(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] font-bold">Yeni İzin Talebi</DialogTitle>
          <DialogDescription className="text-xs text-[#8a7e70]">
            Talep onaylanınca günler puantaja işlenir. Puantajda haftalık izin ya da resmi tatil
            olarak işaretli günler izne çevrilmez ve yıllık izinden düşmez.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {sabitPersonel ? (
            <p className="text-sm font-semibold text-[#1a1916]">{sabitPersonel.ad}</p>
          ) : (
            <div className="space-y-1">
              <Label className="text-xs text-[#8a7e70]">Personel</Label>
              <SearchableSelect
                value={form.employee_id}
                onChange={(v) => setForm(p => ({ ...p, employee_id: v }))}
                options={personeller.map(e => ({
                  value: e.id, label: `${e.first_name || ''} ${e.last_name || ''}`.trim(),
                  hint: e.department_name || '' }))}
                placeholder="Personel seçin" searchPlaceholder="Personel ara..."
                testId="izin-personel" />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs text-[#8a7e70]">İzin Türü</Label>
            <select value={form.izin_turu_id}
              onChange={(e) => setForm(p => ({ ...p, izin_turu_id: e.target.value }))}
              className="w-full h-9 px-3 rounded-md border border-[#d0c4b0] bg-white text-sm"
              data-testid="izin-turu">
              <option value="">Seçiniz</option>
              {turler.map(t => <option key={t.id} value={t.id}>{t.ad}</option>)}
            </select>
            {tur?.azami_gun && (
              <p className="text-[11px] text-[#8a7e70]">Yasal süre: {tur.azami_gun} gün</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-[#8a7e70]">Başlangıç</Label>
              <Input type="date" value={form.baslangic}
                onChange={(e) => setForm(p => ({ ...p, baslangic: e.target.value,
                  bitis: p.bitis < e.target.value ? e.target.value : p.bitis }))}
                className="bg-white border-[#d0c4b0] h-9" data-testid="izin-baslangic" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#8a7e70]">Bitiş</Label>
              <Input type="date" value={form.bitis} min={form.baslangic}
                onChange={(e) => setForm(p => ({ ...p, bitis: e.target.value }))}
                className="bg-white border-[#d0c4b0] h-9" data-testid="izin-bitis" />
            </div>
          </div>
          <p className="text-xs text-[#3F3E39]">
            <b>{gun}</b> takvim günü
            {tur?.azami_gun && gun > tur.azami_gun && (
              <span className="text-[#a34b3f]"> — yasal süreyi aşıyor</span>
            )}
          </p>
          <div className="space-y-1">
            <Label className="text-xs text-[#8a7e70]">Açıklama</Label>
            <Input value={form.aciklama} onChange={(e) => setForm(p => ({ ...p, aciklama: e.target.value }))}
              className="bg-white border-[#d0c4b0] h-9" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#d0c4b0]">Vazgeç</Button>
          <Button onClick={kaydet} disabled={kaydediyor}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="izin-talep-kaydet">
            {kaydediyor ? 'Kaydediliyor…' : 'Talep Oluştur'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── TALEP TABLOSU ───

export function TalepTablosu({ talepler, onayYetkisi, duzenleYetkisi, onDegisti, personelSutunu = true }) {
  const [mesgul, setMesgul] = useState(null);

  const islem = async (t, tur) => {
    let govde;
    if (tur === 'reddet') {
      const neden = window.prompt(`${t.personel_adi} — ${t.izin_turu_adi} talebini reddetme nedeni (boş bırakılabilir):`);
      if (neden === null) return;
      govde = { neden };
    }
    if (tur === 'iptal' && t.durum === 'onaylandi'
        && !window.confirm(`Onaylı izin iptal edilsin mi? Puantajda bu izinle işaretli günler izinden önceki haline döner.`)) return;
    if (tur === 'onayla'
        && !window.confirm(`${t.personel_adi} için ${tarihBicim(t.baslangic)} – ${tarihBicim(t.bitis)} ${t.izin_turu_adi} onaylansın mı? Günler puantaja işlenecek.`)) return;
    setMesgul(t.id);
    try {
      const r = await api.post(`/izin/talepler/${t.id}/${tur}`, govde);
      if (tur === 'onayla') {
        const atlanan = r.data?.atlanan_gunler?.length || 0;
        toast.success(`Onaylandı — ${r.data?.islenen_gun_sayisi ?? 0} gün puantaja işlendi`
          + (atlanan ? `, ${atlanan} gün atlandı (haftalık izin / resmi tatil / kadro dışı)` : ''), { duration: 7000 });
      } else if (tur === 'iptal') {
        toast.success(t.durum === 'onaylandi'
          ? `İptal edildi — ${r.data?.geri_alinan_gun_sayisi ?? 0} gün puantajda geri alındı` : 'Talep geri çekildi');
      } else {
        toast.success('Talep reddedildi');
      }
      onDegisti?.();
    } catch (e) {
      hata(e, 'İşlem başarısız');
    } finally { setMesgul(null); }
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
            {personelSutunu && <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Personel</TableHead>}
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tür</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarihler</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Gün</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
            <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">İşlem</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {talepler.length === 0 && (
            <TableRow><TableCell colSpan={personelSutunu ? 6 : 5} className="text-center text-sm text-[#8a7e70] py-8">
              Kayıt yok
            </TableCell></TableRow>
          )}
          {talepler.map(t => {
            const d = TALEP_DURUM[t.durum] || TALEP_DURUM.beklemede;
            return (
              <TableRow key={t.id} className="border-[#d0c4b0]" data-testid={`izin-talep-${t.id}`}>
                {personelSutunu && <TableCell className="text-sm font-medium text-[#1a1916]">{t.personel_adi}</TableCell>}
                <TableCell className="text-sm text-[#3F3E39]">
                  {t.izin_turu_adi}
                  {t.aciklama && <p className="text-[11px] text-[#8a7e70] truncate max-w-[220px]" title={t.aciklama}>{t.aciklama}</p>}
                </TableCell>
                <TableCell className="text-sm text-[#3F3E39] whitespace-nowrap">
                  {tarihBicim(t.baslangic)} – {tarihBicim(t.bitis)}
                </TableCell>
                <TableCell className="text-sm text-right font-mono">
                  {t.durum === 'onaylandi'
                    ? <span title={`${t.takvim_gunu} takvim günü, ${t.islenen_gun_sayisi} gün puantaja işlendi`}>
                        {t.islenen_gun_sayisi}<span className="text-[#8a7e70]">/{t.takvim_gunu}</span>
                      </span>
                    : t.takvim_gunu}
                </TableCell>
                <TableCell>
                  <span className={`status-badge ${d.sinif}`}>{d.etiket}</span>
                  {t.red_nedeni && <p className="text-[11px] text-[#7a3535] mt-0.5">{t.red_nedeni}</p>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {t.durum === 'beklemede' && onayYetkisi && (
                    <>
                      <Button size="sm" variant="ghost" disabled={mesgul === t.id} title="Onayla"
                        onClick={() => islem(t, 'onayla')} data-testid={`izin-onayla-${t.id}`}>
                        <Check size={15} className="text-[#3d6e4e]" />
                      </Button>
                      <Button size="sm" variant="ghost" disabled={mesgul === t.id} title="Reddet"
                        onClick={() => islem(t, 'reddet')}>
                        <X size={15} className="text-[#a34b3f]" />
                      </Button>
                    </>
                  )}
                  {((t.durum === 'beklemede' && duzenleYetkisi) || (t.durum === 'onaylandi' && onayYetkisi)) && (
                    <Button size="sm" variant="ghost" disabled={mesgul === t.id}
                      title={t.durum === 'onaylandi' ? 'İzni iptal et (puantajı geri al)' : 'Talebi geri çek'}
                      onClick={() => islem(t, 'iptal')}>
                      <Ban size={15} className="text-[#8a7e70]" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── YILLIK İZİN BAKİYESİ ───

export function BakiyeOzeti({ bakiye, onayYetkisi, onDegisti }) {
  const [dokumAcik, setDokumAcik] = useState(false);
  const [hareket, setHareket] = useState(null);   // {tur}
  const [form, setForm] = useState({ tarih: bugunIso(), gun: '', aciklama: '' });
  const [mesgul, setMesgul] = useState(false);

  if (!bakiye) return null;

  const kaydet = async () => {
    setMesgul(true);
    try {
      await api.post('/izin/hareket', { employee_id: bakiye.employee_id, tur: hareket.tur, ...form });
      toast.success(hareket.tur === 'acilis' ? 'Açılış bakiyesi girildi' : 'Düzeltme eklendi');
      setHareket(null); onDegisti?.();
    } catch (e) { hata(e, 'Kaydedilemedi'); } finally { setMesgul(false); }
  };

  const sil = async (id, metin) => {
    if (!window.confirm(`${metin} silinsin mi?`)) return;
    try { await api.delete(`/izin/hareket/${id}`); toast.success('Silindi'); onDegisti?.(); }
    catch (e) { hata(e, 'Silinemedi'); }
  };

  const Kutu = ({ etiket, deger, vurgu, testId }) => (
    <div className={`rounded-lg border p-3 ${vurgu ? 'border-[#3d6e4e] bg-[#3d6e4e]/5' : 'border-[#d0c4b0] bg-white'}`}>
      <p className="text-[11px] uppercase tracking-wide text-[#8a7e70] font-semibold">{etiket}</p>
      <p className={`text-xl font-bold ${vurgu ? 'text-[#3d6e4e]' : 'text-[#1a1916]'}`} data-testid={testId}>{deger}</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {!bakiye.acilis_girildi && (
        <div className="flex gap-2 items-start rounded-md bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 px-3 py-2 text-xs text-[#5c4f22]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            <b>Açılış bakiyesi girilmedi.</b> Sistem öncesinde kullanılan izinler bilinmediği için
            kalan izin gerçekte olduğundan yüksek görünebilir.
          </span>
        </div>
      )}
      {bakiye.uyari && (
        <div className="flex gap-2 items-start rounded-md bg-[#a34b3f]/10 border border-[#a34b3f]/30 px-3 py-2 text-xs text-[#7a3535]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{bakiye.uyari}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kutu etiket="Kalan" deger={`${gunBicim(bakiye.kalan)} gün`} vurgu testId="izin-kalan" />
        <Kutu etiket={bakiye.acilis_girildi ? 'Açılış' : 'Kıdem'}
          deger={bakiye.acilis_girildi ? `${gunBicim(bakiye.acilis.gun)} gün` : `${bakiye.kidem_yil} yıl`} />
        <Kutu etiket="Hak Edilen" deger={`${gunBicim(bakiye.hak_edilen)} gün`} />
        <Kutu etiket="Kullanılan" deger={`${gunBicim(bakiye.kullanilan)} gün`} />
        <Kutu etiket="Planlanan" deger={`${gunBicim(bakiye.planlanan)} gün`} />
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-[#3F3E39]">
        {bakiye.sonraki_hak_edis && (
          <span>Sonraki hak ediş: <b>{tarihBicim(bakiye.sonraki_hak_edis.tarih)}</b> — {bakiye.sonraki_hak_edis.gun} gün
            ({bakiye.sonraki_hak_edis.kidem_yil}. kıdem yılı)</span>
        )}
        <button type="button" onClick={() => setDokumAcik(v => !v)}
          className="flex items-center gap-1 text-[#3d6e4e] hover:underline">
          {dokumAcik ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Hesap dökümü
        </button>
        {onayYetkisi && (
          <span className="ml-auto flex gap-2">
            {!bakiye.acilis_girildi && (
              <Button size="sm" variant="outline" className="h-8 border-[#d0c4b0]"
                onClick={() => { setForm({ tarih: bugunIso(), gun: '', aciklama: '' }); setHareket({ tur: 'acilis' }); }}
                data-testid="izin-acilis-gir">
                <Plus size={13} className="mr-1" /> Açılış bakiyesi
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-8 border-[#d0c4b0]"
              onClick={() => { setForm({ tarih: bugunIso(), gun: '', aciklama: '' }); setHareket({ tur: 'duzeltme' }); }}>
              <Plus size={13} className="mr-1" /> Düzeltme
            </Button>
          </span>
        )}
      </div>

      {dokumAcik && (
        <div className="rounded-lg border border-[#d0c4b0] bg-white text-xs">
          <table className="w-full">
            <tbody>
              {bakiye.acilis && (
                <tr className="border-b border-[#e8dfd0]">
                  <td className="px-3 py-1.5">{tarihBicim(bakiye.acilis.tarih)}</td>
                  <td className="px-3 py-1.5">Açılış bakiyesi (bu tarih sonu itibarıyla kalan){bakiye.acilis.aciklama ? ` — ${bakiye.acilis.aciklama}` : ''}</td>
                  <td className="px-3 py-1.5 text-right font-mono">+{gunBicim(bakiye.acilis.gun)}</td>
                  <td className="px-2 w-8">{onayYetkisi && (
                    <button onClick={() => sil(bakiye.acilis.id, 'Açılış bakiyesi')} title="Sil"><Trash2 size={12} className="text-[#a34b3f]" /></button>)}</td>
                </tr>
              )}
              {bakiye.hak_edisler.map(h => (
                <tr key={h.tarih} className="border-b border-[#e8dfd0]">
                  <td className="px-3 py-1.5">{tarihBicim(h.tarih)}</td>
                  <td className="px-3 py-1.5">{h.kidem_yil}. kıdem yılı hak edişi{h.yas != null ? ` (yaş ${h.yas})` : ''}</td>
                  <td className="px-3 py-1.5 text-right font-mono">+{h.gun}</td><td />
                </tr>
              ))}
              {bakiye.duzeltmeler.map(h => (
                <tr key={h.id} className="border-b border-[#e8dfd0]">
                  <td className="px-3 py-1.5">{tarihBicim(h.tarih)}</td>
                  <td className="px-3 py-1.5">Düzeltme{h.aciklama ? ` — ${h.aciklama}` : ''}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{h.gun > 0 ? '+' : ''}{gunBicim(h.gun)}</td>
                  <td className="px-2 w-8">{onayYetkisi && (
                    <button onClick={() => sil(h.id, 'Düzeltme')} title="Sil"><Trash2 size={12} className="text-[#a34b3f]" /></button>)}</td>
                </tr>
              ))}
              <tr className="border-b border-[#e8dfd0]">
                <td className="px-3 py-1.5" /><td className="px-3 py-1.5">Puantajda yıllık izin (bugüne kadar)</td>
                <td className="px-3 py-1.5 text-right font-mono">−{bakiye.kullanilan}</td><td />
              </tr>
              <tr className="border-b border-[#e8dfd0]">
                <td className="px-3 py-1.5" /><td className="px-3 py-1.5">Puantajda yıllık izin (ileri tarihli, onaylı)</td>
                <td className="px-3 py-1.5 text-right font-mono">−{bakiye.planlanan}</td><td />
              </tr>
              <tr className="font-bold">
                <td className="px-3 py-1.5" /><td className="px-3 py-1.5">Kalan</td>
                <td className="px-3 py-1.5 text-right font-mono">{gunBicim(bakiye.kalan)}</td><td />
              </tr>
            </tbody>
          </table>
          <p className="px-3 py-2 text-[11px] text-[#8a7e70] border-t border-[#e8dfd0]">
            Hak ediş 4857 sayılı Kanun md. 53'e göre hesaplanır: 1–5 yıl 14, 5–15 yıl 20, 15 yıl ve üstü 26 gün;
            18 yaş ve altı ile 50 yaş ve üstü için en az 20 gün.
          </p>
        </div>
      )}

      <Dialog open={!!hareket} onOpenChange={(v) => !v && setHareket(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">
              {hareket?.tur === 'acilis' ? 'Açılış Bakiyesi' : 'İzin Düzeltmesi'} — {bakiye.ad}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#8a7e70]">
              {hareket?.tur === 'acilis'
                ? 'Seçilen tarihin SONU itibarıyla kalan yıllık izin günü. O tarihe kadarki hak edişler ve kullanımlar bu sayının içinde sayılır; sonrası üstüne eklenir.'
                : 'Hesaplanan bakiyeye elle eklenecek (+) ya da düşülecek (−) gün. Açılış tarihinden sonra olmalı.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-[#8a7e70]">Tarih</Label>
                <Input type="date" value={form.tarih} onChange={(e) => setForm(p => ({ ...p, tarih: e.target.value }))}
                  className="bg-white border-[#d0c4b0] h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#8a7e70]">{hareket?.tur === 'acilis' ? 'Kalan gün' : 'Gün (±)'}</Label>
                <Input type="number" step="any" value={form.gun} onChange={(e) => setForm(p => ({ ...p, gun: e.target.value }))}
                  className="bg-white border-[#d0c4b0] h-9" data-testid="izin-hareket-gun" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#8a7e70]">Açıklama</Label>
              <Input value={form.aciklama} onChange={(e) => setForm(p => ({ ...p, aciklama: e.target.value }))}
                className="bg-white border-[#d0c4b0] h-9" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHareket(null)} className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={kaydet} disabled={mesgul || form.gun === ''}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
